import type { ChangeEvent } from 'react'
import { useRef, useState, useEffect } from 'react'

import { faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons/faMagnifyingGlass'
import Button from '@space-core/components/button'
import Loader from '@space-core/components/loader'
import { useDebounce } from '@space-core/hooks/useDebounce'
import useTranslation from '@space-core/hooks/useTranslation'
import { modalUnbanMemberCommunityEventDispatch } from '@space-events/toggleUnbanMemberCommunityModal'
import type { UserBlockedFollowers } from '@space-shell/common/services/communities'
import { useGetBlockedFollowers } from '@space-shell/modules/communities/hooks'
import clsx from 'clsx'

import { MAX_LENGTH_INPUT, MIN_LENGTH_INPUT, TIME_DEBOUNCE } from './constants'
import { Container, CustomIcon, CustomTextField, UsersContainer } from './styles'
import type { ContainerUnbanMemberProps } from './types'

import CardMember from '../cardMember'
import type { EmptyStateTypes } from '../emptyState'
import EmptyStateUnBanMember from '../emptyState'

const ContainerUnbanMember = ({
  communityId = 0,
  fromConfigurationSidebar = false
}: ContainerUnbanMemberProps) => {
  const [searchText, setSearchText] = useState<string>('')
  const inputRef = useRef<HTMLInputElement>(null)
  const [emptyStateType, setEmptyStateType] = useState<EmptyStateTypes | ''>('')

  const debouncedSearchText = useDebounce({ value: searchText, delay: TIME_DEBOUNCE })

  const handleOnSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.currentTarget.value)
  }

  const {
    data,
    isLoading,
    refetch,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    isError
  } = useGetBlockedFollowers(debouncedSearchText, communityId)

  const { t } = useTranslation()

  useEffect(() => {
    setEmptyStateType('')
    refetch()
  }, [debouncedSearchText, refetch])

  useEffect(() => {
    if (isError) {
      return setEmptyStateType('ERROR_LOAD_LIST')
    }

    if (!isError && data && data?.pages[0]?.data.length === 0 && !hasNextPage) {
      if (searchText.length > 0) {
        return setEmptyStateType('USER_NOT_FIND')
      } else {
        return setEmptyStateType('EMPTY_BAN_LIST')
      }
    }

    return setEmptyStateType('')
  }, [data, hasNextPage, isError, searchText])

  const handleOpenUnbanMemberModal = (user: UserBlockedFollowers) => {
    modalUnbanMemberCommunityEventDispatch({ action: 'close' })
    modalUnbanMemberCommunityEventDispatch({
      action: 'openConfirmation',
      user,
      fromConfigurationSidebar
    })
  }

  return (
    <>
      <div className='hot-form _w-full _mb-4 _pb-0 _w-full'>
        <CustomTextField
          ref={inputRef}
          name='inputSearch'
          type='text'
          onChange={handleOnSearchChange}
          className='hot-form__input hot-form__input--md _position-relative _pr-7'
          placeholder={t('modal.ban_list.placeholder_search')}
          aria-label={t('modal.ban_list.placeholder_search')}
          autoComplete='off'
          maxLength={MAX_LENGTH_INPUT}
          min={MIN_LENGTH_INPUT}
        />

        <CustomIcon icon={faMagnifyingGlass} />
      </div>

      <Container className='_d-flex _flex-column _justify-content-center align-items-center _w-full'>
        {isLoading ? (
          <Loader full />
        ) : (
          <>
            <UsersContainer
              className={clsx(' _mb-5', {
                '_d-flex _flex-column _justify-content-center align-items-center':
                  !hasNextPage && emptyStateType
              })}>
              {data?.pages.map(({ data: users }, indexPages) => {
                return users.length === 0 && !hasNextPage && emptyStateType ? (
                  <EmptyStateUnBanMember key={indexPages} type={emptyStateType} />
                ) : (
                  users?.map((user, indexUser) => (
                    <CardMember key={indexUser} user={user} onClick={handleOpenUnbanMemberModal} />
                  ))
                )
              })}
            </UsersContainer>
          </>
        )}
      </Container>

      {hasNextPage ? (
        <div className='_d-flex _align-items-center _justify-content-center _my-5 _w-full'>
          <Button
            type='button'
            variation='default'
            onClick={() => fetchNextPage()}
            loading={isFetching || isFetchingNextPage}>
            {t('buttons.load_more')}
          </Button>
        </div>
      ) : null}
    </>
  )
}

export default ContainerUnbanMember
